import { useEffect, useRef } from 'react'

import { FormProvider, type UseFormReturn } from 'react-hook-form'

import { LevelPlay } from '@/shared/ui/LevelPlay'

import type { Step } from './stepTypes'

/**
 * Shell do step ativo do Level Play, compartilhado por jornada e quiz.
 *
 * Substitui o `react-step-wizard`: os dois `<div>` que a lib interpunha entre
 * este componente e o `<form>` não eram contêineres flex nem tinham altura, o
 * que rompia a corrente que ancora o footer na base. Com a lib fora, o `<form>`
 * é o contêiner da coluna.
 *
 * A montagem é exclusiva (equivalente ao `isLazyMount`): só o step ativo existe
 * na árvore, e a `key` por `step.id` força desmontagem ao navegar — é isso que
 * reseta contagem regressiva, player de vídeo e estado de tabuleiro entre
 * atividades.
 *
 * **O que é injetado é exatamente o que resta de domínio:** o registro de kinds,
 * o lookup da atividade, e os componentes de Footer e Toolbar. Nada aqui sabe se
 * está numa jornada ou num quiz.
 */

interface ActivityKindConfig {
  Component: React.ComponentType<{ step: Step; activity: unknown }>
  handleAnswerCallback?: (data: Record<string, unknown>) => Record<string, unknown>
  interactionMode?: unknown
  customContent?: boolean
}

export interface LevelPlayActivityProps {
  steps: Step[]
  currentStepIndex: number
  methods: UseFormReturn
  /** Resolve a atividade (desafio / pergunta) a partir do `activityId` do step. */
  getActivity: (activityId?: number) => any
  /** Registro de kinds do domínio — já composto com o catálogo compartilhado. */
  getConfigByKind: (kind: string) => ActivityKindConfig
  /** Submete a resposta já normalizada pelo `handleAnswerCallback` do kind. */
  onVerifyAnswer: (data: Record<string, unknown> & { uid: string }) => Promise<void>
  Toolbar: React.ComponentType<{ activity: unknown }>
  Footer: React.ComponentType<{
    id: string
    step: Step
    activity: unknown
    interactionMode?: unknown
  }>
}

export const LevelPlayActivity = ({
  steps,
  currentStepIndex,
  methods,
  getActivity,
  getConfigByKind,
  onVerifyAnswer,
  Toolbar,
  Footer
}: LevelPlayActivityProps) => {
  // Direção da última navegação, só para escolher o sentido da transição de
  // entrada — espelha o `fadeInRight`/`fadeInLeft` que a lib aplicava.
  const previousStepIndex = useRef(currentStepIndex)
  const goingBack = currentStepIndex < previousStepIndex.current

  useEffect(() => {
    previousStepIndex.current = currentStepIndex
  }, [currentStepIndex])

  const step = steps[currentStepIndex]
  // Resolvida aqui em cima — não só dentro de `renderStep` — porque a Toolbar
  // (Corações + Contagem) também precisa da atividade corrente, e ela monta
  // fora do corpo do step.
  const activity = step ? getActivity(step.activityId) : undefined

  const renderStep = () => {
    // O índice pode sair da faixa enquanto a lista de steps ainda carrega. A
    // toolbar segue de pé — só o corpo não monta.
    if (!step) return null

    const {
      Component: ActivityComponent,
      handleAnswerCallback,
      interactionMode,
      customContent = false
    } = getConfigByKind(step.kind)

    const onSubmitAnswer = async (data: Record<string, unknown>) => {
      let newData = { ...data }

      if (handleAnswerCallback)
        newData = handleAnswerCallback({ ...data, activity })

      await onVerifyAnswer({ ...newData, uid: activity.uid })
    }

    return (
      <FormProvider key={step.id} {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmitAnswer)}
          className={`flex min-h-0 flex-1 flex-col ${
            goingBack ? 'animate-step-in-back' : 'animate-step-in-forward'
          }`}
        >
          {!customContent && (
            <LevelPlay.Header id={step.id} step={step} challenge={activity} />
          )}
          {customContent ? (
            <ActivityComponent step={step} activity={activity} />
          ) : (
            <LevelPlay.Body id={step.id}>
              <ActivityComponent step={step} activity={activity} />
            </LevelPlay.Body>
          )}
          {!customContent && (
            <Footer
              id={step.id}
              step={step}
              activity={activity}
              interactionMode={interactionMode}
            />
          )}
        </form>
      </FormProvider>
    )
  }

  return (
    <LevelPlay.Screen>
      <Toolbar activity={activity} />
      {renderStep()}
    </LevelPlay.Screen>
  )
}
